import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils/cn';

type Props = {
  surah: number;
  startAyah: number;
  endAyah?: number;
  /** Marks the citation as not verified against the retrieved set. */
  invalid?: boolean;
};

/**
 * Inline ayah citation. Renders `(S:V)` or `(S:V-V)` as a small,
 * accent-colored link to the reader. When `invalid` is true (citation
 * not present in the retrieved set), we still render the text but
 * dim it and remove the link — better than vanishing it silently.
 */
export function CitationLink({ surah, startAyah, endAyah, invalid }: Props) {
  const label =
    endAyah && endAyah !== startAyah
      ? `${surah}:${startAyah}-${endAyah}`
      : `${surah}:${startAyah}`;

  if (invalid) {
    return (
      <span
        title="This citation could not be verified against the retrieved verses."
        className="num-tight text-ink-400 dark:text-ink-500 line-through"
      >
        ({label})
      </span>
    );
  }

  return (
    <Link
      to={`/surah/${surah}/ayah/${startAyah}`}
      aria-label={`Surah ${surah}, ayah ${startAyah}${endAyah ? ' to ' + endAyah : ''}, opens in reader`}
      className={cn(
        'num-tight',
        'text-accent dark:text-accent-400',
        'hover:underline underline-offset-2',
        'transition-colors',
      )}
    >
      ({label})
    </Link>
  );
}
